import { AddBox, CloseRounded } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  styled
} from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { settinguserHierarchy } from '../../../../../../redux/actions/users'
import { IRootState } from '../../../../../../redux/types'
import useScreenWidth from '../../../../../shared/hooks/useScreenWidth'
import { StyledDialog } from '../../../../../shared/styles/StyledDialog'
import { isAdmin, isManager } from '../../../utils/RolesHelper'

export const LinkedList = styled(List)(({ theme }) => ({
  maxHeight: 77,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    backgroundColor: theme.palette.grey[200],
    borderRadius: '4px',
    width: '6px',
    margin: '8px',
    '&-thumb': {
      backgroundColor: theme.palette.grey[500],
      borderRadius: '4px'
    }
  }
}))

export default function ParentsManagement() {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const dispatch = useDispatch()

  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { userProfileSelected } = useSelector((state: IRootState) => state.user)
  const { roles, rolehierarchy } = useSelector((state: IRootState) => state.role)

  const [anchorOp, setAnchorOp] = useState<null | HTMLElement>(null)
  const [openOptions, setOpenOptions] = useState(-1)
  const [openLinked, setOpenLinked] = useState(false)
  const [roleLinked, setRoleLinked] = useState<number | null>(null)
  const [userHierarchies, setUserHierarchies] = useState([])

  const menuLinked = (id: number) => (event: MouseEvent<HTMLDivElement>) => {
    setAnchorOp(event.currentTarget)
    setOpenOptions(id)
  }

  const handleCloseOptions = () => {
    setAnchorOp(null)
    setOpenOptions(-1)
  }
  const handleClose = () => {
    setOpenLinked(false)
  }

  const settingUserHierarchy = (action: number) => (_: any, value: any) => {
    dispatch(
      settinguserHierarchy({
        user_id: userProfileSelected.id,
        parent_id: value.id,
        action
      })
    )
    setRoleLinked(null)
    handleClose()
    handleCloseOptions()
  }

  let parentsId: number[] = []
  let flag: boolean = true
  const hasParents = userProfileSelected.parents.length > 0
  return (
    <Grid xs={12} md={3}>
      {hasParents && (
        <Box>
          <Box display={'flex'} alignItems={'center'} justifyContent={isMobile ? '' : 'space-between'}>
            <Typography style={{ fontWeight: 500, textTransform: 'capitalize' }}>{`${t('admin.people.associate-superior')}`}</Typography>
            {isManager(authUser.role_id) && isMobile ? (
              <Button
                onClick={() => {
                  setOpenLinked(!openLinked)
                }}
                variant="outlined"
                size="small"
                color="inherit"
                startIcon={<AddBox htmlColor={'#8DDD55'} />}
                style={{ marginLeft: 8 }}
              >
                {`${t('add')}`}
              </Button>
            ) : (
              <Tooltip title={`${t('admin.people.assign-superior')}`}>
                <IconButton
                  onClick={() => {
                    setOpenLinked(!openLinked)
                  }}
                >
                  <AddBox htmlColor={'#8DDD55'} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <LinkedList dense aria-label="linked users">
            {userProfileSelected.parents
              .filter((parent: any) => !isAdmin(parent.role_id))
              .map((userhierarchy: any) => (
                <>
                  <ListItemButton
                    key={userhierarchy.id}
                    aria-controls="simple-menu"
                    divider
                    aria-haspopup="false"
                    onClick={menuLinked(userhierarchy.id)}
                  >
                    <ListItemText
                      primary={
                        <Tooltip title={userhierarchy.role.name} TransitionComponent={Fade} TransitionProps={{ timeout: 600 }}>
                          <Typography variant="body2" align="center">
                            {userhierarchy.name}
                          </Typography>
                        </Tooltip>
                      }
                    />
                    {isManager(authUser.role_id) && (
                      <Menu
                        id="simple-linked-menu"
                        anchorEl={anchorOp}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        open={userhierarchy.id === openOptions}
                        onClick={handleCloseOptions}
                        onClose={handleCloseOptions}
                      >
                        <MenuItem onClick={() => settingUserHierarchy(0)(null, userhierarchy)}>{t('admin.people.unlink')}</MenuItem>
                      </Menu>
                    )}
                  </ListItemButton>
                  {userhierarchy.parents
                    .filter((parent: any) => !isAdmin(parent.role_id))
                    .map((parent: any) => {
                      if (parentsId.includes(parent.id)) {
                        flag = false
                      } else {
                        parentsId.push(parent.id)
                        flag = true
                      }

                      if (flag)
                        return (
                          <ListItemButton
                            key={parent.id}
                            aria-controls="simple-menu"
                            divider
                            aria-haspopup="true"
                            onClick={menuLinked(parent.id)}
                          >
                            <ListItemText
                              primary={
                                <Tooltip title={parent.role.name} TransitionComponent={Fade} TransitionProps={{ timeout: 600 }}>
                                  <Typography variant="body2" align="center">
                                    {`> ${parent.name}`}
                                  </Typography>
                                </Tooltip>
                              }
                            />
                            {[1, 2, 3].includes(authUser.role_id) && (
                              <Menu
                                id="simple-linked-menu"
                                anchorEl={anchorOp}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                open={parent.id === openOptions}
                                onClick={handleCloseOptions}
                                onClose={handleCloseOptions}
                              >
                                <MenuItem onClick={() => settingUserHierarchy(0)(null, parent)}>{t('admin.people.unlink')}</MenuItem>
                              </Menu>
                            )}
                          </ListItemButton>
                        )
                    })}
                </>
              ))}
          </LinkedList>
        </Box>
      )}

      <StyledDialog
        open={openLinked}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <Grid container justifyContent="space-between">
            <Typography variant="h6">{`${t('admin.people.assign-superior')}`}</Typography>
            <Button onClick={handleClose} color="primary" variant="text">
              <CloseRounded />
            </Button>
          </Grid>
        </DialogTitle>

        <DialogContent sx={{ pb: 3, pt: 2 }}>
          <form noValidate autoComplete="nope">
            <Grid container alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Grid xs={12}>
                <FormControl fullWidth variant="filled">
                  <InputLabel shrink={true} id="lbl-roles">
                    {t('rol')}
                  </InputLabel>
                  <Select
                    required
                    value={roleLinked}
                    labelId="lbl-roles"
                    id="combo-box-roles"
                    fullWidth
                    label="role"
                    onChange={(event: any) => {
                      setRoleLinked(event.target.value)
                      setUserHierarchies(
                        authUser.sons.filter((userhierarchy: any) => {
                          if (event.target.value === userhierarchy.role_id) return userhierarchy
                        })
                      )
                    }}
                  >
                    {roles
                      .filter((role: any) => rolehierarchy.includes(role.id) && role.id !== 4)
                      .map((role: any) => {
                        return (
                          <MenuItem key={role.id} value={role.id}>
                            {role.name}
                          </MenuItem>
                        )
                      })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid xs={12}>
                <Autocomplete
                  id="combo-box-usershierarchies"
                  value={null}
                  onChange={settingUserHierarchy(1)}
                  options={userHierarchies}
                  fullWidth
                  getOptionLabel={(option: any) => (option.id !== undefined ? option.name : '')}
                  renderInput={params => <TextField {...params} label={t('users')} variant="filled" InputLabelProps={{ shrink: true }} />}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </StyledDialog>
    </Grid>
  )
}
